import { Alert, Badge, Button, Group, NumberInput, SimpleGrid, Stack, Switch, Text } from '@mantine/core';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api, podatki, type Shema } from '../api/odjemalec';
import { NapakaApi, sporocilo } from '../api/napaka';
import { denar, stevilo } from '../oblika/oblika';
import { Kartica, Podatki, StanjeNalaganja } from '../postavitev/Podatki';
import { KLJUC_AI } from './ai';
import { PorabaTock } from './UmetnaInteligencaSkrbnistvo';
import { glave, obNapaki } from './zeton';

/**
 * Podrobnosti instance → Umetna inteligenca: na voljo je samodejno (plača se s točkami, vsaj 1 točka); skrbnik jo lahko
 * instanci prepove in ji določi posebno mesečno omejitev žetonov. Prikaže tudi, ali jo je stranka izklopila, in porabo
 * v tem mesecu. Povezavo, cenik in navodila ureja zavihek Umetna inteligenca v skrbništvu.
 */
export function AiInstance({ companyId }: { companyId: number }) {
  const ai = useQuery({
    queryKey: [...KLJUC_AI, 'instanca', companyId],
    queryFn: async ({ signal }) => {
      try {
        return await podatki(
          api.GET('/api/skrbnik/instance/{companyId}/ai', {
            signal,
            headers: glave(),
            params: { path: { companyId } },
          }),
        );
      } catch (e) {
        obNapaki(e);
        throw e;
      }
    },
  });
  const povezava = useQuery({
    queryKey: KLJUC_AI,
    queryFn: async ({ signal }) => {
      try {
        return await podatki(api.GET('/api/skrbnik/ai', { signal, headers: glave() }));
      } catch (e) {
        obNapaki(e);
        throw e;
      }
    },
  });
  if (!ai.data) {
    return (
      <Kartica naslov="Umetna inteligenca">
        <StanjeNalaganja
          napaka={ai.error}
          ponovi={() => void ai.refetch()}
          naslov="Nastavitve umetne inteligence ni bilo mogoče naložiti"
        />
      </Kartica>
    );
  }
  return (
    <Urejanje
      key={`${ai.data.prepovedano}-${ai.data.omejitevTock ?? ''}`}
      i={ai.data}
      povezano={povezava.data?.povezano}
      privzeta={povezava.data?.omejitevTock}
    />
  );
}

function Urejanje({ i, povezano, privzeta }: { i: Shema['AiInstanca']; povezano?: boolean; privzeta?: number }) {
  const odjemalec = useQueryClient();
  const [prepovedano, setPrepovedano] = useState(!!i.prepovedano);
  const [omejitev, setOmejitev] = useState<number | string>(i.omejitevTock ?? '');
  const [napake, setNapake] = useState<Record<string, string>>({});
  const [napaka, setNapaka] = useState<string | null>(null);
  const [dela, setDela] = useState(false);
  const spremenjeno = prepovedano !== !!i.prepovedano || (omejitev === '' ? undefined : omejitev) !== i.omejitevTock;

  const shrani = async () => {
    setDela(true);
    setNapake({});
    setNapaka(null);
    try {
      const nova = await podatki(
        api.PUT('/api/skrbnik/instance/{companyId}/ai', {
          headers: glave(),
          params: { path: { companyId: i.companyId! } },
          body: { prepovedano, omejitevTock: typeof omejitev === 'number' ? omejitev : undefined },
        }),
      );
      odjemalec.setQueryData([...KLJUC_AI, 'instanca', i.companyId], nova);
      await odjemalec.invalidateQueries({ queryKey: [...KLJUC_AI, 'poraba'] });
    } catch (e) {
      obNapaki(e);
      if (e instanceof NapakaApi && Object.keys(e.polja).length > 0) {
        setNapake(e.polja);
      } else {
        setNapaka(sporocilo(e));
      }
    } finally {
      setDela(false);
    }
  };

  return (
    <Kartica
      naslov="Umetna inteligenca"
      dejanja={
        i.prepovedano ? (
          <Badge color="red" variant="light">
            prepovedano
          </Badge>
        ) : i.izklopljeno ? (
          <Badge color="gray" variant="light">
            stranka izklopila
          </Badge>
        ) : (
          <Badge color="green" variant="light">
            na voljo
          </Badge>
        )
      }
    >
      <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md">
        <Stack gap="sm">
          {povezano === false && (
            <Alert color="yellow" variant="light">
              Povezava z jezikovnim modelom ni nastavljena (Skrbništvo, zavihek Umetna inteligenca).
            </Alert>
          )}
          <Switch
            label="Prepovej umetno inteligenco"
            description="Sicer je na voljo samodejno (plača se s točkami, potrebna je vsaj 1 točka); stranka jo lahko izklopi v Nastavitvah."
            checked={prepovedano}
            onChange={(e) => setPrepovedano(e.currentTarget.checked)}
          />
          <NumberInput
            size="md"
            label="Mesečna omejitev (točke)"
            description="Največ toliko točk na mesec za umetno inteligenco. Prazno = privzeta omejitev iz nastavitve povezave."
            placeholder={privzeta ? `Privzeta (${denar(privzeta)})` : 'Privzeta (brez omejitve)'}
            value={omejitev}
            onChange={setOmejitev}
            min={0.01}
            decimalScale={2}
            thousandSeparator="."
            decimalSeparator=","
            hideControls
            error={napake.omejitevTock}
          />
          {napaka && (
            <Alert color="red" variant="light" role="alert">
              {napaka}
            </Alert>
          )}
          <Group justify="flex-end">
            <Button onClick={() => void shrani()} loading={dela} disabled={!spremenjeno}>
              Shrani
            </Button>
          </Group>
        </Stack>
        <Stack gap="xs">
          <Text size="sm" fw={600}>
            Ta mesec
          </Text>
          <PorabaTock zaracunano={i.zaracunano} omejitev={i.veljavnaOmejitev} zetoni={i.porabljeno} />
          <Podatki
            vrstice={[
              ['Klicev', stevilo(i.klicev ?? 0)],
              ['Neuspešnih', stevilo(i.napak ?? 0)],
            ]}
          />
        </Stack>
      </SimpleGrid>
    </Kartica>
  );
}
